// POST + fetch reader 기반 SSE 파서 — app.js/dcf.js 에 각각 구현돼 있던 것을 통합.
// (EventSource 는 GET 전용이라 쓸 수 없다 — 채팅은 multipart POST 스트리밍)

import { authHeaders } from "./api";

export interface SseEvent {
  event: string;
  [k: string]: unknown;
}

export interface StreamOptions {
  /** 서버 heartbeat(8~15s)조차 없으면 죽은 연결로 보고 끊는 시간 */
  watchdogMs?: number;
  signal?: AbortSignal;
}

/** POST 하고 `data: {json}\n\n` 스트림을 SseEvent 로 순차 방출한다. */
export async function* postSse(
  path: string,
  body: FormData,
  opts: StreamOptions = {},
): AsyncGenerator<SseEvent> {
  const ctrl = new AbortController();
  const outer = opts.signal;
  if (outer) outer.addEventListener("abort", () => ctrl.abort(outer.reason), { once: true });

  const watchdogMs = opts.watchdogMs ?? 60000;
  let watchdog: ReturnType<typeof setTimeout> | undefined;
  const feed = () => {
    if (watchdog) clearTimeout(watchdog);
    watchdog = setTimeout(() => ctrl.abort("stalled"), watchdogMs);
  };

  try {
    const resp = await fetch(path, {
      method: "POST",
      body,
      headers: authHeaders(),
      signal: ctrl.signal,
    });
    if (resp.status === 401) throw new Error("unauthorized");
    if (!resp.ok || !resp.body) throw new Error(`HTTP ${resp.status}`);

    const reader = resp.body.getReader();
    const decoder = new TextDecoder();
    let buf = "";
    feed();
    for (;;) {
      const { value, done } = await reader.read();
      if (done) break;
      feed();
      buf += decoder.decode(value, { stream: true });
      let idx: number;
      while ((idx = buf.indexOf("\n\n")) >= 0) {
        const chunk = buf.slice(0, idx);
        buf = buf.slice(idx + 2);
        const line = chunk.split("\n").find((l) => l.startsWith("data:"));
        if (!line) continue;
        try {
          yield JSON.parse(line.slice(5).trim()) as SseEvent;
        } catch {
          /* 잘린 프레임 — 무시 */
        }
      }
    }
  } finally {
    if (watchdog) clearTimeout(watchdog);
  }
}
